import React from 'react';
import { Field, Fields, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Form, Header } from 'semantic-ui-react';
import { renderInput, renderDropbox, renderRadioGroup, renderDate, renderPhone } from '../FormFields';
import { states } from '../../utils/states';
import {
  getGenders,
} from '../../utils/dates';
import { str2bool, formatDate, formatPhone, scrollOnSubmitError } from '../../utils/utils';
import { validateEnroll, maxLength, validateExp } from '../../utils/validation';
import _ from 'lodash';
import ExternalModal from '../Modals/ExternalModal';

const patientValidation = (values) => {
  const errors = validateEnroll(values, false);
  return errors;
};

const PatientInformationForm = props => {
  const { handleSubmit, onSubmit, submitFailed, invalid } = props;
  const sanitizeForm = (values) => {
    const finalForm = _.pick(values, ['firstName', 'lastName', 'address1', 'address2', 'city', 'state', 'zip', 'email', 'gender', 'pharmacyInsurance.pbmName', 'pharmacyInsurance.memberId', 'pharmacyInsurance.bin', 'pharmacyInsurance.group', 'pharmacyInsurance.pcn', 'prescriber.firstName', 'prescriber.lastName', 'prescriber.address1', 'prescriber.address2', 'prescriber.city', 'prescriber.state', 'prescriber.zip']);
    finalForm.mobilePhoneNumber = formatPhone(values.areaCode, values.firstThreeDigits, values.lastFourDigits);
    finalForm.dob = formatDate(values.month, values.day, values.year);
    finalForm.hasMarketingOptIn = str2bool(values.hasMarketingOptIn);
    finalForm.hasPermissionOptIn = str2bool(values.hasPermissionOptIn);
    onSubmit({ values: finalForm, path: `/patient/${props.formType}/finish` });
  };

  const submitBtnHandler = () => {
    scrollOnSubmitError(invalid);
  };

  let productString = '';
  let diseaseString = '';
  switch (props.environment) {
    case 'alunbrig':
      productString = 'Alunbrig (brigatinib)';
      diseaseString = 'ALK+ non–small cell lung cancer';
      break;
    case 'fruzaqla':
      productString = 'Fruzaqla (fruquintinib)';
      diseaseString = 'metastatic colorectal cancer';
      break;
    case 'iclusig':
      productString = 'Iclusig (ponatinib)';
      diseaseString = 'chronic myeloid leukemia or Philadelphia chromosome-positive acute lymphoblastic leukemia';
      break;
    case 'ninlaro':
      productString = 'Ninlaro (ixazomib)';
      diseaseString = 'multiple myeloma';
      break;
    case 'mimrylo':
      productString = 'Mimrylo (rusfertide)';
      diseaseString = 'polycythemia vera';
      break;
    default:
      productString = '';
      diseaseString = '';
  }

  return (
    <div className="patient-information form">
      <p className="required">Required field.</p>

      <Form className="ui form" onSubmit={ handleSubmit(sanitizeForm) }>
        <Header as="h2" className="opt-in-header mt-0">
          Patient Information
        </Header>

        <Field
          required
          name="firstName"
          type="text"
          component={ renderInput }
          label="First Name"
          errorMessage="First Name is a required field."
          allowNameCharsOnly={ true }
        />
        <Field
          required
          name="lastName"
          type="text"
          component={ renderInput }
          label="Last Name"
          errorMessage="Last Name is a required field."
          allowNameCharsOnly={ true }
        />
        <Field
          required
          label="Street Address - Line 1"
          errorMessage="Street Address is a required field."
          type="text"
          name="address1"
          component={ renderInput }
        />
        <Field
          label="Street Address - Line 2"
          type="text"
          name="address2"
          component={ renderInput }
        />
        <Field
          required
          label="City"
          errorMessage="City is a required field."
          type="text"
          name="city"
          component={ renderInput }
        />
        <Form.Group>
          <Field
            required
            label="State"
            errorMessage="State is a required field."
            name="state"
            customClass="states"
            options={ states }
            component={ renderDropbox }
          />
          <Field
            required
            label="ZIP Code"
            errorMessage="ZIP Code is a required field."
            invalidErrorMessage="ZIP Code is invalid."
            type="tel"
            name="zip"
            customClass="zip-code"
            component={ renderInput }
            min="0"
            normalize={ (value, previousValue) => {
              value = maxLength(5)(value, previousValue);
              if (!validateExp({ type: 'justNumbers', value })) {
                return previousValue;
              }
              return value;
            } }
          />
        </Form.Group>
        <Fields
          required
          names={ ['month', 'day', 'year'] }
          component={ renderDate }
          errorMessage="Date of Birth is a required field."
          invalidErrorMessage="Date of Birth is invalid."
          label="Date of Birth"
        />
        <Field
          label="Email Address"
          type="email"
          name="email"
          errorMessage="Email is invalid."
          component={ renderInput }
        />
        <Field
          label="Confirm Email Address"
          type="email"
          name="confirmEmail"
          errorMessage="Email entry does not match."
          component={ renderInput }
        />
        <Fields
          required
          names={ ['areaCode', 'firstThreeDigits', 'lastFourDigits'] }
          component={ renderPhone }
          errorMessage="Phone Number is a required field."
          invalidErrorMessage="Phone Number is invalid."
          label="Phone Number"
        />
        <Field
          required
          label="Gender"
          errorMessage="Gender is a required field."
          name="gender"
          customClass="gender"
          options={ getGenders }
          component={ renderDropbox }
        />

        <Header as="h2" className="opt-in-header">
          Prescription Insurance Information
        </Header>

        <Field
          required
          name="pharmacyInsurance.pbmName"
          type="text"
          component={ renderInput }
          label="Insurance Name"
          errorMessage="Insurance Name is a required field."
        />

        <Field
          required
          name="pharmacyInsurance.memberId"
          type="text"
          component={ renderInput }
          label="Rx Member ID Number"
          errorMessage="Rx Member ID Number is a required field."
          alphaNumericOnly={ true }
        />

        <Field
          required
          name="pharmacyInsurance.bin"
          type="text"
          component={ renderInput }
          label="Rx BIN Number"
          errorMessage="Rx Bin Number is a required field."
          invalidErrorMessage="Rx Bin Number is invalid."
          normalize={ (value, previousValue) => {
            value = maxLength(6)(value, previousValue);
            if (!validateExp({ type: 'justNumbers', value })) {
              return previousValue;
            }
            return value;
          } }
        />

        <Field
          name="pharmacyInsurance.group"
          type="text"
          component={ renderInput }
          label="Rx Group Number"
        />

        <Field
          name="pharmacyInsurance.pcn"
          type="text"
          component={ renderInput }
          label="Rx PCN Number"
          alphaNumericOnly={ true }
        />

        <div className="enroll-note">
          <p>Note: Please input your primary insurance information in the fields above if no Rx specific insurance is available.</p>
          <p>If you don’t have insurance, you can enroll into Takeda Oncology Here2Assist <ExternalModal url="https://www.here2assist.com/enroll" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">online</a> }/> or call for assistance at 1-844-817-6468, Option 2, Monday-Friday, 8AM-8PM ET.</p>
        </div>

        <Header as="h2" className="opt-in-header">
          Doctor & Office Information (Optional)
        </Header>

        <Field
          name="prescriber.firstName"
          type="text"
          component={ renderInput }
          label="Doctor First Name"
          allowNameCharsOnly={ true }
        />

        <Field
          name="prescriber.lastName"
          type="text"
          component={ renderInput }
          label="Doctor Last Name"
          allowNameCharsOnly={ true }
        />

        <Field
          label="Office Street Address - Line 1"
          type="text"
          name="prescriber.address1"
          component={ renderInput }
        />
        <Field
          label="Office Street Address - Line 2"
          type="text"
          name="prescriber.address2"
          component={ renderInput }
        />
        <Field
          label="Office City"
          type="text"
          name="prescriber.city"
          component={ renderInput }
        />
        <Form.Group>
          <Field
            label="Office State"
            name="prescriber.state"
            customClass="states"
            options={ states }
            component={ renderDropbox }
          />
          <Field
            label="Office ZIP Code"
            type="tel"
            name="prescriber.zip"
            customClass="zip-code"
            component={ renderInput }
            min="0"
            errorMessage="ZIP Code is invalid."
            invalidErrorMessage="ZIP Code is invalid."
            normalize={ (value, previousValue) => {
              value = maxLength(5)(value, previousValue);
              if (!validateExp({ type: 'justNumbers', value })) {
                return previousValue;
              }
              return value;
            } }
          />
        </Form.Group>

        <p>By providing my information above and clicking submit, I certify I am at least 18 years old and understand that Takeda and its business partners will use my personal information to enroll me in the Takeda Oncology Co-Pay Assistance Program and provide the support I am asking for. </p>

        <Header as="h2" className="opt-in-header has-radios">
            Marketing Opt-in:
          <Header.Subheader className="required">
            Additionally, I authorize Takeda, its affiliates, and business partners to use my personal information to provide information and offers related to {diseaseString}, {productString} and the conditions it treats, and related treatment options. In addition to information about {productString} and related health conditions, I understand this may include information from Takeda, financial assistance programs, clinical trials and market research opportunities, and other support services or programs Takeda may in the future develop for patients.
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasPermissionOptIn'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasPermissionOptIn"
          customClass="permission-opt"
        />
        <Header as="h2" className="opt-in-header has-radios">
          <Header.Subheader className="required">
            In addition to the information about {diseaseString} and {productString}, I also agree to receive product and disease-state information from Takeda, its affiliates, service providers, and copromotion partners. Takeda may use this information to provide me with information, resources, and communications. My information may also be used for market research purposes.
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasMarketingOptIn'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasMarketingOptIn"
          customClass="promotions-opt"
        />

        <p>Your privacy is important to us. For more information, please refer to our <ExternalModal url="https://www.takeda.com/privacy-notice/" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Privacy Notice</a> }/>.</p>

        <p>You may opt out at any time by clicking on the “unsubscribe” link provided in our communications.</p>

        <p>By submitting this form, you agree to Takeda’s <ExternalModal url="https://www.takeda.com/terms-of-use" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Terms of Use</a> }/> and <ExternalModal url="https://www.takeda.com/privacy-notice/" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Privacy Notice</a> }/>.</p>

        <button
          onClick={ submitBtnHandler }
          type="submit" className= { `ui button form-button track ${props.submitTrackId} track-env-${props.environment}` }
          disabled={ submitFailed && invalid }
        >
          Submit
        </button>
      </Form>
    </div>
  );
};

PatientInformationForm.propTypes = {
  treatment: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  onSubmit: PropTypes.func.isRequired,
  submitTrackId: PropTypes.string.isRequired,
};

export default reduxForm({
  form: 'patientInformationForm', // a unique identifier for this form
  validate: patientValidation, // <--- validation function given to redux-form
})(PatientInformationForm);
